import {
  type BreakdownOption,
  METRICS,
  type MetricType,
  type TimeBucketOption,
} from 'features/charts-v2/types'
import { ChartTypeV2 } from 'features/charts-v2/types/chart-types'
import { getChartTypePreset } from 'features/charts-v2/types/ChartPresets'

/**
 * Defines constraints for a specific chart type
 */
export interface ChartConstraints {
  metrics: {
    count: {
      min: number
      max: number
    }
    options: Record<MetricType, boolean>
  }
  breakdowns: {
    min: number
    max: number
    invalidTypes?: BreakdownOption[]
  }
  timeBucket?: {
    required?: boolean
  }
}

/**
 * Registry of chart constraints by chart type
 */
export const CHART_CONSTRAINTS: Record<ChartTypeV2, ChartConstraints> = {
  [ChartTypeV2.PIE]: {
    metrics: {
      count: {
        min: 1,
        max: 1,
      },
      options: {
        [METRICS.RECORD_COUNT.id]: true,
        [METRICS.SENTENCE_COUNT.id]: true,
        [METRICS.SENTIMENT.id]: false,
        [METRICS.RATING.id]: false,
        [METRICS.NET_PROMOTER_SCORE.id]: false,
      },
    },
    breakdowns: {
      min: 0,
      max: 1,
    },
  },
  [ChartTypeV2.BAR_VERTICAL]: {
    metrics: {
      count: {
        min: 1,
        max: 2,
      },
      options: {
        [METRICS.RECORD_COUNT.id]: true,
        [METRICS.SENTENCE_COUNT.id]: true,
        [METRICS.SENTIMENT.id]: true,
        [METRICS.RATING.id]: true,
        [METRICS.NET_PROMOTER_SCORE.id]: false,
      },
    },
    breakdowns: {
      min: 0,
      max: 1,
    },
  },
  [ChartTypeV2.BAR_HORIZONTAL]: {
    metrics: {
      count: {
        min: 1,
        max: 1,
      },
      options: {
        [METRICS.RECORD_COUNT.id]: true,
        [METRICS.SENTENCE_COUNT.id]: true,
        [METRICS.SENTIMENT.id]: true,
        [METRICS.RATING.id]: true,
        [METRICS.NET_PROMOTER_SCORE.id]: false,
      },
    },
    breakdowns: {
      min: 0,
      max: 1,
    },
  },
  [ChartTypeV2.RIDGELINE]: {
    metrics: {
      count: {
        min: 1,
        max: 1,
      },
      options: {
        [METRICS.RECORD_COUNT.id]: false,
        [METRICS.SENTENCE_COUNT.id]: false,
        [METRICS.SENTIMENT.id]: true,
        [METRICS.RATING.id]: true,
        [METRICS.NET_PROMOTER_SCORE.id]: false,
      },
    },
    breakdowns: {
      min: 0,
      max: 1,
    },
  },
  // Raw breakdown count only; the 2-4 expanded-column bound (a grouped
  // breakdown is two columns) lives in `countSankeyColumns` / validateSankey.
  [ChartTypeV2.SANKEY]: {
    metrics: {
      count: {
        min: 1,
        max: 1,
      },
      options: {
        [METRICS.RECORD_COUNT.id]: true,
        [METRICS.SENTENCE_COUNT.id]: true,
        [METRICS.SENTIMENT.id]: false,
        [METRICS.RATING.id]: false,
        [METRICS.NET_PROMOTER_SCORE.id]: false,
      },
    },
    breakdowns: {
      min: 1,
      max: 3,
    },
  },
  [ChartTypeV2.LINE]: {
    metrics: {
      count: {
        min: 1,
        max: 2,
      },
      options: {
        [METRICS.RECORD_COUNT.id]: true,
        [METRICS.SENTENCE_COUNT.id]: true,
        [METRICS.SENTIMENT.id]: true,
        [METRICS.RATING.id]: true,
        [METRICS.NET_PROMOTER_SCORE.id]: false,
      },
    },
    breakdowns: {
      min: 0,
      max: 1,
    },
    timeBucket: {
      required: true,
    },
  },
  [ChartTypeV2.BAR_HORIZONTAL_STACKED]: {
    metrics: {
      count: {
        min: 1,
        max: 1,
      },
      options: {
        [METRICS.RECORD_COUNT.id]: true,
        [METRICS.SENTENCE_COUNT.id]: true,
        [METRICS.SENTIMENT.id]: false,
        [METRICS.RATING.id]: false,
        [METRICS.NET_PROMOTER_SCORE.id]: false,
      },
    },
    breakdowns: {
      min: 0,
      max: 2,
    },
  },
  [ChartTypeV2.SCATTER]: {
    metrics: {
      count: {
        min: 2,
        max: 2,
      },
      options: {
        [METRICS.RECORD_COUNT.id]: true,
        [METRICS.SENTENCE_COUNT.id]: true,
        [METRICS.SENTIMENT.id]: true,
        [METRICS.RATING.id]: true,
        [METRICS.NET_PROMOTER_SCORE.id]: false,
      },
    },
    breakdowns: {
      min: 0,
      max: 1,
    },
  },
  [ChartTypeV2.BUBBLE]: {
    metrics: {
      count: {
        min: 2,
        max: 3,
      },
      options: {
        [METRICS.RECORD_COUNT.id]: true,
        [METRICS.SENTENCE_COUNT.id]: true,
        [METRICS.SENTIMENT.id]: true,
        [METRICS.RATING.id]: true,
        [METRICS.NET_PROMOTER_SCORE.id]: false,
      },
    },
    breakdowns: {
      min: 0,
      max: 1,
    },
  },
  [ChartTypeV2.TABLE]: {
    metrics: {
      count: {
        min: 1,
        max: 10,
      },
      options: {
        [METRICS.RECORD_COUNT.id]: true,
        [METRICS.SENTENCE_COUNT.id]: true,
        [METRICS.SENTIMENT.id]: true,
        [METRICS.RATING.id]: true,
        [METRICS.NET_PROMOTER_SCORE.id]: false,
      },
    },
    breakdowns: {
      min: 0,
      max: 5,
    },
  },
  [ChartTypeV2.HEATMAP]: {
    metrics: {
      count: {
        min: 1,
        max: 2,
      },
      options: {
        [METRICS.RECORD_COUNT.id]: true,
        [METRICS.SENTENCE_COUNT.id]: true,
        [METRICS.SENTIMENT.id]: true,
        [METRICS.RATING.id]: true,
        [METRICS.NET_PROMOTER_SCORE.id]: false,
      },
    },
    breakdowns: {
      min: 2,
      max: 2,
    },
  },
  [ChartTypeV2.TREEMAP]: {
    metrics: {
      count: {
        min: 1,
        max: 4,
      },
      options: {
        [METRICS.RECORD_COUNT.id]: true,
        [METRICS.SENTENCE_COUNT.id]: true,
        [METRICS.SENTIMENT.id]: true,
        [METRICS.RATING.id]: true,
        [METRICS.NET_PROMOTER_SCORE.id]: true,
      },
    },
    breakdowns: {
      min: 0,
      max: 1,
    },
  },
  [ChartTypeV2.AREA]: {
    metrics: {
      count: {
        min: 1,
        max: 2,
      },
      options: {
        [METRICS.RECORD_COUNT.id]: true,
        [METRICS.SENTENCE_COUNT.id]: true,
        [METRICS.SENTIMENT.id]: true,
        [METRICS.RATING.id]: true,
        [METRICS.NET_PROMOTER_SCORE.id]: false,
      },
    },
    breakdowns: {
      min: 0,
      max: 1,
    },
    timeBucket: {
      required: true,
    },
  },
  [ChartTypeV2.AREA_STACKED]: {
    metrics: {
      count: {
        min: 1,
        max: 1,
      },
      options: {
        [METRICS.RECORD_COUNT.id]: true,
        [METRICS.SENTENCE_COUNT.id]: true,
        [METRICS.SENTIMENT.id]: false,
        [METRICS.RATING.id]: false,
        [METRICS.NET_PROMOTER_SCORE.id]: false,
      },
    },
    breakdowns: {
      min: 0,
      max: 2,
    },
    timeBucket: {
      required: true,
    },
  },
  [ChartTypeV2.AREA_PERCENT]: {
    metrics: {
      count: {
        min: 1,
        max: 1,
      },
      options: {
        [METRICS.RECORD_COUNT.id]: true,
        [METRICS.SENTENCE_COUNT.id]: true,
        [METRICS.SENTIMENT.id]: false,
        [METRICS.RATING.id]: false,
        [METRICS.NET_PROMOTER_SCORE.id]: false,
      },
    },
    breakdowns: {
      min: 0,
      max: 2,
    },
    timeBucket: {
      required: true,
    },
  },
  [ChartTypeV2.HISTOGRAM]: {
    metrics: {
      count: {
        min: 1,
        max: 1,
      },
      options: {
        [METRICS.RECORD_COUNT.id]: true,
        [METRICS.SENTENCE_COUNT.id]: true,
        [METRICS.SENTIMENT.id]: true,
        [METRICS.RATING.id]: true,
        [METRICS.NET_PROMOTER_SCORE.id]: false,
      },
    },
    breakdowns: {
      min: 0,
      max: 1,
    },
  },
  [ChartTypeV2.DEPRECATED_V1]: {
    metrics: {
      count: { min: 0, max: 0 },
      options: {
        [METRICS.RECORD_COUNT.id]: false,
        [METRICS.SENTENCE_COUNT.id]: false,
        [METRICS.SENTIMENT.id]: false,
        [METRICS.RATING.id]: false,
        [METRICS.NET_PROMOTER_SCORE.id]: false,
      },
    },
    breakdowns: { min: 0, max: 0 },
  },
  [ChartTypeV2.BAR_VERTICAL_STACKED]: {
    metrics: {
      count: { min: 0, max: 0 },
      options: {
        [METRICS.RECORD_COUNT.id]: false,
        [METRICS.SENTENCE_COUNT.id]: false,
        [METRICS.SENTIMENT.id]: false,
        [METRICS.RATING.id]: false,
        [METRICS.NET_PROMOTER_SCORE.id]: false,
      },
    },
    breakdowns: { min: 0, max: 0 },
  },
  [ChartTypeV2.UNKNOWN]: {
    metrics: {
      count: { min: 0, max: 0 },
      options: {
        [METRICS.RECORD_COUNT.id]: false,
        [METRICS.SENTENCE_COUNT.id]: false,
        [METRICS.SENTIMENT.id]: false,
        [METRICS.RATING.id]: false,
        [METRICS.NET_PROMOTER_SCORE.id]: false,
      },
    },
    breakdowns: { min: 0, max: 0 },
  },
}

/**
 * Service to get constraints for a specific chart type
 */
export class ChartConstraintsService {
  /**
   * Get constraints for a specific chart type
   */
  static getConstraints(chartType: ChartTypeV2): ChartConstraints {
    const constraints = CHART_CONSTRAINTS[chartType]
    if (!constraints) {
      // Return default constraints for unknown chart types
      return {
        metrics: {
          count: { min: 1, max: 10 },
          options: {
            [METRICS.RECORD_COUNT.id]: true,
            [METRICS.SENTENCE_COUNT.id]: true,
            [METRICS.SENTIMENT.id]: true,
            [METRICS.RATING.id]: true,
            [METRICS.NET_PROMOTER_SCORE.id]: false,
          },
        },
        breakdowns: { min: 0, max: 5 },
      }
    }
    return constraints
  }

  /**
   * Check if a metric is allowed for a chart type
   */
  static isMetricAllowed(chartType: ChartTypeV2, metric: MetricType): boolean {
    const constraints = this.getConstraints(chartType)
    return constraints.metrics.options[metric] ?? false
  }

  /**
   * Check if a breakdown is allowed for a chart type
   */
  static isBreakdownAllowed(
    chartType: ChartTypeV2,
    breakdown: BreakdownOption,
  ): boolean {
    const constraints = this.getConstraints(chartType)
    if (!constraints.breakdowns.invalidTypes) {
      return true // No restrictions
    }
    return !constraints.breakdowns.invalidTypes.includes(breakdown)
  }

  /**
   * Get the reason why a breakdown is invalid for a chart type
   */
  static getBreakdownInvalidReason(
    chartType: ChartTypeV2,
    breakdown: BreakdownOption,
  ): string | null {
    if (this.isBreakdownAllowed(chartType, breakdown)) {
      return null
    }

    return `${breakdown} is not valid for ${chartType} charts`
  }

  /**
   * Check if current metric selections are at maximum for a chart type
   */
  static areMetricsAtMax(
    chartType: ChartTypeV2,
    currentSelections: MetricType[],
  ): boolean {
    const constraints = this.getConstraints(chartType)
    return currentSelections.length >= constraints.metrics.count.max
  }

  /**
   * Check if current breakdown selections are at maximum for a chart type
   */
  static areBreakdownsAtMax(
    chartType: ChartTypeV2,
    currentSelections: BreakdownOption[],
  ): boolean {
    const constraints = this.getConstraints(chartType)
    return currentSelections.length >= constraints.breakdowns.max
  }

  /**
   * Get tooltip message for when metrics are at maximum
   */
  static getMetricMaxTooltip(chartType: ChartTypeV2): string {
    const constraints = this.getConstraints(chartType)
    if (constraints.metrics.count.max === 1) {
      return `Only one metric may be selected for ${this.getChartTypeLabel(chartType)} charts`
    }
    return `Maximum of ${constraints.metrics.count.max} metrics allowed for ${this.getChartTypeLabel(chartType)} charts`
  }

  static getBreakdownMaxTooltip(chartType: ChartTypeV2): string {
    const constraints = this.getConstraints(chartType)
    const max = constraints.breakdowns.max
    return `${this.getChartTypeLabel(chartType)} charts support a maximum of ${max} ${max > 1 ? 'breakdowns' : 'breakdown'}`
  }

  /**
   * Get default metrics for a specific chart type
   */
  static getDefaultMetrics(chartType: ChartTypeV2): MetricType[] {
    const preset = getChartTypePreset(chartType)
    return preset?.metrics || [METRICS.RECORD_COUNT.id]
  }

  /**
   * Get default breakdowns for a specific chart type
   */
  static getDefaultBreakdowns(chartType: ChartTypeV2): BreakdownOption[] {
    const preset = getChartTypePreset(chartType)
    if (!preset) return []

    const breakdowns: BreakdownOption[] = []
    if (preset.defaultPrimaryBreakdown) {
      breakdowns.push(preset.defaultPrimaryBreakdown)
    }
    if (preset.secondaryBreakdown) {
      breakdowns.push(preset.secondaryBreakdown)
    }
    return breakdowns
  }

  /**
   * Get default time bucket for a specific chart type
   */
  static getDefaultTimeBucket(chartType: ChartTypeV2): TimeBucketOption | null {
    const preset = getChartTypePreset(chartType)
    return preset?.timeBucket || null
  }

  /**
   * Check if a chart type requires a time bucket
   */
  static requiresTimeBucket(chartType: ChartTypeV2): boolean {
    const constraints = this.getConstraints(chartType)
    return constraints.timeBucket?.required || false
  }

  /**
   * Get human-readable chart type label
   */
  private static getChartTypeLabel(chartType: ChartTypeV2): string {
    switch (chartType) {
      case ChartTypeV2.PIE:
        return 'Pie'
      case ChartTypeV2.BAR_HORIZONTAL:
        return 'Bar'
      case ChartTypeV2.BAR_VERTICAL:
        return 'Vertical Bar'
      case ChartTypeV2.LINE:
        return 'Line'
      case ChartTypeV2.SCATTER:
        return 'Scatter'
      case ChartTypeV2.BUBBLE:
        return 'Bubble'
      case ChartTypeV2.HEATMAP:
        return 'Heatmap'
      case ChartTypeV2.TREEMAP:
        return 'Treemap'
      case ChartTypeV2.RIDGELINE:
        return 'Ridgeline'
      case ChartTypeV2.SANKEY:
        return 'Sankey'
      case ChartTypeV2.BAR_HORIZONTAL_STACKED:
        return 'Stacked Bar'
      case ChartTypeV2.TABLE:
        return 'Table'
      default:
        return chartType
    }
  }
}
